import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { Modal } from '../../components/Modal/Modal'
import React, { useMemo, useState } from 'react'
import { Col, Container, Row, Spinner, Stack } from 'react-bootstrap'
import { Button } from '../../components/Button/Button'
import { RoomSession } from '../../models/Room'
import { CreatorIcon } from '../../apps/professional-app/fa/PastMeetings/CreatorIcon'
import { MeetingDateDisplay } from '../LiveMeetings/MeetingDateDisplay'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { parseMetadata } from '../../common/util'
import { MeetingTitleDisplay } from '../LiveMeetings/MeetingTitleDisplay'
import { MeetingCreatorDisplay } from '../LiveMeetings/MeetingCreatorDisplay'
import { MeetingParticipantsDisplay } from '../LiveMeetings/MeetingParticipantsDisplay'
import { useGetPhoneCandidatesQuery } from '../../apps/professional-app/fa/store/fa'
import { ManageCallModal } from './ManageCallModal'

export type SelectPhoneMeetingModalProps = {
  show: boolean
  phoneConfig: MeetingProviderAccountIntegrationConfig
  doCall: boolean
  onHide: () => void
}

export const SelectPhoneMeetingModal: React.FC<SelectPhoneMeetingModalProps> = ({ show, phoneConfig, doCall, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const [selectedSession, setSelectedSession] = useState<RoomSession | null>(null)
  const [selectionMade, setSelectionMade] = useState<boolean>(false)
  const { data: phoneSessionCandidates, isLoading: phoneSessionCandidatesLoading } = useGetPhoneCandidatesQuery(
    { accountId, pageRequest: { page: 0, size: 5 } },
    { refetchOnMountOrArgChange: true },
  )

  const handleOnHide = () => {
    onHide()
  }

  interface PhoneMeetingCardProps {
    session: RoomSession
    onSessionSelected: (session: RoomSession) => void
  }

  const PhoneMeetingCard: React.FC<PhoneMeetingCardProps> = ({ session, onSessionSelected }) => {
    const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
    const { creator_service_id: creatorServiceId, external: { title = null } = {} } = useMemo(() => parseMetadata(session.metadata), [session])

    return (
      <Container className='d-flex flex-column p-3 border zocks-border-color-gray-200 zocks-bg-color-gray-100 rounded-1 gap-2'>
        <Stack gap={1}>
          <Row className='meeting-status-row m-0 align-items-center gap-2'>
            <Col xs='auto' className='p-0'>
              {creatorServiceId && <CreatorIcon creator={creatorServiceId} />}
            </Col>
            <Col xs='auto' className='p-0'>
              <MeetingDateDisplay startedAt={session.startedAt} finishedAt={session.finishedAt} />
            </Col>
          </Row>
          <Row className='m-0 align-items-center'>
            <Col className='meeting-title p-0'>
              <MeetingTitleDisplay title={title} />{' '}
            </Col>
          </Row>
          <Row className='d-flex flex-row m-0 align-items-center'>
            <Col className='d-flex flex-row gap-3 paragraph-medium medium upcoming-created-by-name p-0'>
              <MeetingCreatorDisplay creatorId={currentUserId} userLabels={[{ id: currentUserId, label: 'Me' }]} />
              <MeetingParticipantsDisplay creatorId={session.roomCreatedBy?.id} users={session.users} />
            </Col>
          </Row>
        </Stack>
        <Row className='d-flex flex-row m-0 align-items-center'>
          <Col className='p-0' xs='auto'>
            <Button variant='primary' className='button-small' onClick={() => onSessionSelected(session)}>
              {doCall ? 'Start for this meeting' : 'Add to this meeting'}
            </Button>
          </Col>
        </Row>
      </Container>
    )
  }

  const body = phoneSessionCandidatesLoading ? (
    <div className='d-flex align-items-center justify-content-center'>
      <Spinner />
    </div>
  ) : (
    <Container className='d-flex flex-column gap-3'>
      <Row>
        <Col>
          We found matching meeting(s) in your calendar. Choose one, or{' '}
          <Button variant='link' className='d-inline text-decoration-none zocks-text-primary button-medium' onClick={() => setSelectionMade(true)}>
            {doCall ? 'start a new meeting' : 'add the AI Assistant to a new meeting'}
          </Button>
          .
        </Col>
      </Row>
      <Row className='d-flex flex-column gap-3'>
        {phoneSessionCandidates?.data &&
          phoneSessionCandidates.data.map((roomSession) => <PhoneMeetingCard session={roomSession} onSessionSelected={setSelectedSession} />)}
      </Row>
    </Container>
  )

  const footer = (
    <Container fluid className='p-0'>
      <Row className='d-flex align-items-center m-0'>
        <Col className='p-0'>
          <a
            href='https://help.zocks.io/en/articles/10226330-how-do-i-use-zocks-with-a-shared-line-or-a-software-based-phone-solution'
            target='_blank'
            rel='noreferrer'
          >
            <Stack direction='horizontal' gap={2}>
              <i className='bi bi-info-square zocks-text-gray-900' />
              <div className='paragraph-medium zocks-text-gray-900'>Help Center</div>
            </Stack>
          </a>
        </Col>
        <Col className='p-0'>
          <Button variant={'secondary'} className='label-medium bold ms-auto' onClick={onHide}>
            Close
          </Button>
        </Col>
      </Row>
    </Container>
  )

  return selectionMade || !!selectedSession || (phoneSessionCandidates?.data && !phoneSessionCandidates?.data.length) ? (
    <ManageCallModal show={true} phoneConfig={phoneConfig} doCall={doCall} onHide={handleOnHide} session={selectedSession} />
  ) : (
    <Modal
      show={show}
      onHide={handleOnHide}
      header={<h4>{phoneSessionCandidatesLoading ? 'Searching for matching meetings' : 'Matching meeting(s) found'}</h4>}
      body={body}
      footer={footer}
      centered={true}
      className='select-phone-meeting-modal'
      autoFocus={false}
    />
  )
}
